'use client';

import { c as _c } from "react/compiler-runtime";
import { Box } from '@mui/material';
import { useShallow } from 'zustand/shallow';
import { useOffersFilterStore } from '../store/Context';
import { useFilteredAndSortedOffers } from '../store/hooks/useFilteredAndSortedOffers';
import type { OffersFilterStoreState } from '../store/types';
type FilterResultsAnnouncerProps<T extends {
  id: string;
}> = {
  allOffers: T[];
  featuredSequence: string[];
};

/**
 * Renders a visually hidden aria-live region that announces the filtered results
 * count when filters change. This helps screen reader users know that their
 * filter selection (e.g. "No annual fee") successfully updated the results.
 */
export function FilterResultsAnnouncer(t0) {
  const $ = _c(6);
  const {
    allOffers,
    featuredSequence
  } = t0;
  const areFiltersEnabled = useOffersFilterStore(useShallow(_temp));
  let t1;
  if ($[0] !== allOffers || $[1] !== featuredSequence) {
    t1 = {
      allOffers,
      featuredSequence
    };
    $[0] = allOffers;
    $[1] = featuredSequence;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    offers
  } = useFilteredAndSortedOffers(t1);
  if (!areFiltersEnabled) {
    return null;
  }
  const count = offers.length;
  const message = count === 1 ? "Showing 1 result" : `Showing ${count} results`;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      clip: "rect(0 0 0 0)",
      clipPath: "inset(50%)",
      height: "1px",
      overflow: "hidden",
      position: "absolute",
      whiteSpace: "nowrap",
      width: "1px"
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== message) {
    t3 = <Box component="div" aria-live="polite" aria-atomic="true" sx={t2}>{message}</Box>;
    $[4] = message;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function _temp(state) {
  return state.areFiltersEnabled;
}